import { get, post, put } from "@sal/webrequest-web";
import { assertApiSuccess, getApiErrorMessage, isApiErrorResponse } from "code/apiError";
import { PersonnelUser } from "models/PersonnelUser";
import { PersonnelUserDetail } from "models/PersonnelUserDetail";
import { CommunicationPreferenceUpdate } from "models/communicationPreference";
import { useMemo } from "react";
import useSWR, { mutate } from "swr";
import useSWRMutation from "swr/mutation";

const fetcher = async (url: string) =>
	assertApiSuccess(await get(url), "Failed to load personnel.");

const options = {
	revalidateIfStale: false,
	revalidateOnFocus: false,
	revalidateOnReconnect: false
};

export function useGetPersonnelUsers() {
	const {
		data,
		isLoading,
		error,
		isValidating,
		mutate: mutateData
	} = useSWR("api/Personnel/List", fetcher, options);
	const rows = useMemo(() => (data as PersonnelUser[]) ?? [], [data]);

	return useMemo(
		() => ({
			data: rows,
			isDataLoading: isLoading,
			dataError: error,
			dataValidating: isValidating,
			dataEmpty: !isLoading && rows.length === 0,
			mutateData
		}),
		[rows, error, isLoading, isValidating, mutateData]
	);
}

export type AddPersonnelUserRequest = {
	firstName: string;
	lastName: string;
	email: string;
	phoneNumber?: string;
	roles: string[];
	active: boolean;
	communicationPreferences?: CommunicationPreferenceUpdate[];
};

type MutationResult =
	| { success: true; warningMessage?: string }
	| { success: false; errorMessage: string };
type CreateMutationResult =
	| { success: true; user: PersonnelUserDetail; warningMessage?: string }
	| { success: false; errorMessage: string };

type UpsertPersonnelResponse = {
	user: PersonnelUserDetail;
	warningMessage?: string;
};

function getUpsertPersonnelResponse(
	result: unknown,
	fallbackMessage: string
): UpsertPersonnelResponse {
	if (isApiErrorResponse(result)) {
		throw new Error(getApiErrorMessage(result, fallbackMessage));
	}

	if (result && typeof result === "object" && "user" in result) {
		const partialSuccess = result as {
			user: PersonnelUserDetail;
			commPrefsError?: string;
		};

		return {
			user: partialSuccess.user,
			warningMessage:
				typeof partialSuccess.commPrefsError === "string"
					? partialSuccess.commPrefsError
					: undefined
		};
	}

	if (result && typeof result === "object" && "message" in result) {
		throw new Error(getApiErrorMessage(result, fallbackMessage));
	}

	if (result) {
		return { user: result as PersonnelUserDetail };
	}

	throw new Error(fallbackMessage);
}

const createPersonnelFetcher = async (
	_key: string,
	{ arg }: { arg: AddPersonnelUserRequest }
): Promise<UpsertPersonnelResponse> => {
	const result = await post("api/Personnel/AddUser", arg);
	return getUpsertPersonnelResponse(result, "Failed to create personnel user.");
};

export function useCreatePersonnelUser() {
	const { trigger: triggerCreate, isMutating: isCreating } = useSWRMutation(
		"api/Personnel/List",
		createPersonnelFetcher
	);

	const createPersonnelUser = async (
		payload: AddPersonnelUserRequest
	): Promise<CreateMutationResult> => {
		try {
			const result = await triggerCreate(payload);
			return {
				success: true,
				user: result!.user,
				warningMessage: result.warningMessage
			} as const;
		} catch (error) {
			const errorMessage =
				error instanceof Error ? error.message : "Failed to create personnel user.";

			console.error("Failed to create personnel user", error);

			return { success: false, errorMessage } as const;
		}
	};

	return { createPersonnelUser, isCreating };
}

const deactivatePersonnelFetcher = async (_key: string, { arg }: { arg: number }) => {
	assertApiSuccess(
		await post(`api/Personnel/DeactivateUser/${arg}`, {}),
		"Failed to deactivate personnel user."
	);
};

export function useDeactivatePersonnelUser() {
	const { trigger: triggerDeactivate, isMutating: isDeactivating } = useSWRMutation(
		"api/Personnel/List",
		deactivatePersonnelFetcher
	);

	const deactivatePersonnelUser = async (userId: number): Promise<MutationResult> => {
		try {
			await triggerDeactivate(userId);
			mutate("api/Fleet/List");
			return { success: true } as const;
		} catch (error) {
			const errorMessage =
				error instanceof Error ? error.message : "Failed to deactivate personnel user.";

			console.error("Failed to deactivate personnel user", error);

			return { success: false, errorMessage } as const;
		}
	};

	return { deactivatePersonnelUser, isDeactivating };
}

const activatePersonnelFetcher = async (_key: string, { arg }: { arg: number }) => {
	assertApiSuccess(
		await post(`api/Personnel/ActivateUser/${arg}`, {}),
		"Failed to activate personnel user."
	);
};

export function useActivatePersonnelUser() {
	const { trigger: triggerActivate, isMutating: isActivating } = useSWRMutation(
		"api/Personnel/List",
		activatePersonnelFetcher
	);

	const activatePersonnelUser = async (userId: number): Promise<MutationResult> => {
		try {
			await triggerActivate(userId);
			mutate("api/Fleet/List");
			return { success: true } as const;
		} catch (error) {
			const errorMessage =
				error instanceof Error ? error.message : "Failed to activate personnel user.";

			console.error("Failed to activate personnel user", error);

			return { success: false, errorMessage } as const;
		}
	};

	return { activatePersonnelUser, isActivating };
}

const approvePersonnelFetcher = async (_key: string, { arg }: { arg: number }) => {
	assertApiSuccess(
		await post(`api/Personnel/ApproveUser/${arg}`, {}),
		"Failed to approve personnel user."
	);
};

export function useApprovePersonnelUser() {
	const { trigger: triggerApprove, isMutating: isApproving } = useSWRMutation(
		"api/Personnel/List",
		approvePersonnelFetcher
	);

	const approvePersonnelUser = async (userId: number): Promise<MutationResult> => {
		try {
			await triggerApprove(userId);
			return { success: true } as const;
		} catch (error) {
			const errorMessage =
				error instanceof Error ? error.message : "Failed to approve personnel user.";

			console.error("Failed to approve personnel user", error);

			return { success: false, errorMessage } as const;
		}
	};

	return { approvePersonnelUser, isApproving };
}

export type BulkStatusUpdateFailure = { id: number; error: string };
export type BulkStatusUpdateResult = { succeeded: number[]; failed: BulkStatusUpdateFailure[] };

export async function bulkDeactivatePersonnelUsersApi(
	ids: number[]
): Promise<BulkStatusUpdateResult> {
	return assertApiSuccess<BulkStatusUpdateResult>(
		await post("api/Personnel/BulkDeactivateUsers", { ids }),
		"Failed to deactivate personnel users."
	);
}

export async function bulkActivatePersonnelUsersApi(
	ids: number[]
): Promise<BulkStatusUpdateResult> {
	return assertApiSuccess<BulkStatusUpdateResult>(
		await post("api/Personnel/BulkActivateUsers", { ids }),
		"Failed to activate personnel users."
	);
}

export type EditPersonnelUserWithPrefsRequest = {
	firstName: string;
	lastName: string;
	email: string;
	phoneNumber?: string;
	roles: string[];
	active: boolean;
	communicationPreferences?: CommunicationPreferenceUpdate[];
};

export function useGetPersonnelUserDetail(userId: number | null) {
	const key = userId !== null ? `api/Personnel/${userId}` : null;
	const { data, isLoading, error } = useSWR(key, fetcher, {
		revalidateIfStale: false,
		revalidateOnFocus: false,
		revalidateOnReconnect: false
	});

	return {
		userDetail: (data as PersonnelUserDetail | undefined) ?? null,
		isLoading,
		error
	};
}

const editUserWithPrefsFetcher = async (
	_key: string,
	{ arg }: { arg: { id: number; payload: EditPersonnelUserWithPrefsRequest } }
): Promise<UpsertPersonnelResponse> => {
	const result = await put(`api/Personnel/EditUser/${arg.id}`, arg.payload);
	return getUpsertPersonnelResponse(result, "Failed to update personnel user.");
};

export function useEditPersonnelUserWithPrefs() {
	const { trigger: triggerEdit, isMutating: isEditing } = useSWRMutation(
		"api/Personnel/List",
		editUserWithPrefsFetcher
	);

	const editPersonnelUserWithPrefs = async (
		id: number,
		payload: EditPersonnelUserWithPrefsRequest
	): Promise<MutationResult> => {
		try {
			const result = await triggerEdit({ id, payload });
			void mutate(`api/Personnel/${id}`);
			void mutate("api/Fleet/List");
			return { success: true, warningMessage: result.warningMessage } as const;
		} catch (error) {
			const errorMessage =
				error instanceof Error ? error.message : "Failed to update personnel user.";

			console.error("Failed to update personnel user", error);

			return { success: false, errorMessage } as const;
		}
	};

	return { editPersonnelUserWithPrefs, isEditing };
}

export type NotificationType = {
	notificationTypeId: number;
	systemName: string;
	name: string;
	/** Tooltip / helper text from CV.NotificationType.Description. */
	description?: string | null;
};

export function useGetNotificationTypes(shouldFetch = true) {
	const { data, isLoading, error } = useSWR<NotificationType[]>(
		shouldFetch ? "api/Personnel/NotificationTypes" : null,
		fetcher,
		options
	);

	return useMemo(
		() => ({
			notificationTypes: data ?? [],
			isLoading,
			error
		}),
		[data, isLoading, error]
	);
}
